import { useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Box, IconButton, Typography, Skeleton } from "@mui/material";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type PDFSource = string | ArrayBuffer | { data: Uint8Array } | Blob;

interface PDFViewerProps {
  source: PDFSource;
  width?: number;
}

interface PageDimensions {
  width: number;
  height: number;
}

export default function PDFViewer({ source, width = 800 }: PDFViewerProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageDimensions, setPageDimensions] = useState<PageDimensions | null>(null);
  const [isPageLoading, setIsPageLoading] = useState<boolean>(true);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
  }

  function onPageLoadSuccess(page: { width: number; height: number; originalWidth: number; originalHeight: number }) {
    // Calculate the scaled height based on the desired width
    const scale = width / page.originalWidth;
    const scaledHeight = page.originalHeight * scale;
    
    setPageDimensions({
      width,
      height: scaledHeight,
    });
    setIsPageLoading(false);
  }

  function onPageRenderError() {
    setIsPageLoading(false);
  }

  // Reset loading state when page changes
  const handlePageChange = (newPage: number) => {
    setIsPageLoading(true);
    setPageNumber(newPage);
  };

  const skeletonHeight = pageDimensions?.height || width * 1.414; // Default to A4 aspect ratio

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        maxWidth: "100%",
      }}
    >
      <Document
        file={source}
        onLoadSuccess={onDocumentLoadSuccess}
        loading={
          <Skeleton
            variant="rectangular"
            sx={{ width, height: skeletonHeight, borderRadius: 1 }}
          />
        }
      >
        <Box sx={{ position: "relative", width, minHeight: skeletonHeight }}>
          {isPageLoading && (
            <Skeleton
              variant="rectangular"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width,
                height: skeletonHeight,
                borderRadius: 1,
              }}
            />
          )}
          <Page
            pageNumber={pageNumber}
            width={width}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            onLoadSuccess={onPageLoadSuccess}
            onRenderError={onPageRenderError}
            loading={<></>} // We handle loading ourselves
          />
        </Box>
      </Document>

      {numPages > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", mt: 2, gap: 2 }}>
          <IconButton
            disabled={pageNumber <= 1}
            onClick={() => handlePageChange(pageNumber - 1)}
            aria-label="Previous page"
          >
            <NavigateBeforeIcon />
          </IconButton>

          <Typography
            sx={(t) => t.nawy.typography.english.variants.body.md.regular}
            aria-live="polite"
            aria-atomic="true"
          >
            {pageNumber} / {numPages}
          </Typography>

          <IconButton
            disabled={pageNumber >= numPages}
            onClick={() => handlePageChange(pageNumber + 1)}
            aria-label="Next page"
          >
            <NavigateNextIcon />
          </IconButton>
        </Box>
      )}    </Box>
  );
}